/**
 * @prism-graphics/bridge/protocol: the wire protocol between PRISM (the host page) and the PRISM Graphics editor
 * (the iframe), shared by both sides. See docs/bridge-api.md.
 *
 *   { protocol: 'prism-graphics', version: 1, type, requestId?, payload }
 *
 * Validation is hand-written (no runtime dependencies): `validateMessage` checks the envelope, the protocol
 * version, the type and its payload. Unknown extra keys in a payload are allowed (and ignored), so a newer peer
 * within protocol version 1 can add optional fields.
 */

export const PROTOCOL = 'prism-graphics';
export const PROTOCOL_VERSION = 1;
/** Protocol versions this package speaks. */
export const SUPPORTED_VERSIONS: readonly number[] = Object.freeze([1]);

/** Message types PRISM sends to the editor. */
export const HOST_MESSAGE_TYPES = ['host:init', 'host:token', 'host:theme', 'host:request-save', 'host:request-close'] as const;
/** Message types the editor sends to PRISM (`editor:close-cancelled` is an addition to the brief's table). */
export const EDITOR_MESSAGE_TYPES = ['editor:ready', 'editor:dirty', 'editor:saved', 'editor:close', 'editor:close-cancelled', 'editor:error'] as const;

export type HostMessageType = (typeof HOST_MESSAGE_TYPES)[number];
export type EditorMessageType = (typeof EDITOR_MESSAGE_TYPES)[number];
export type MessageType = HostMessageType | EditorMessageType;

export const ERROR_CODES = [
  'SAVE_FAILED',
  'SAVE_CANCELLED',
  'VERSION_CONFLICT_UNRESOLVED',
  'PERMISSION_DENIED',
  'TOKEN_EXPIRED',
  'TOKEN_REFRESH_FAILED',
  'INIT_INVALID',
  'NOT_READY',
  'TIMEOUT',
] as const;
export type BridgeErrorCode = (typeof ERROR_CODES)[number];

export type Theme = 'light' | 'dark';

export interface Token {
  /** Opaque editor token (PRISM: `pge_…`). Never put it in a URL or a log. */
  value: string;
  /** ISO 8601. */
  expiresAt: string;
}

export interface FrameRate {
  num: number;
  den: number;
}

export interface HostInit {
  token: Token;
  user: { id: string; name: string; locale: string };
  theme: Theme;
  mode: 'new' | 'edit';
  /** Required when mode = 'edit'. */
  assetId?: string;
  /** Optional: the collection a new graphic is saved into. */
  folderId?: string;
  defaults?: {
    width: number;
    height: number;
    frameRate: FrameRate;
    /**
     * Since 1.1.0: further layouts a new graphic starts with (ADR 0037), one per shape, e.g. the other formats of a
     * multi-format sequence. `width × height` above is the primary layout. At most 8; an older editor ignores it.
     */
    layouts?: Array<{ width: number; height: number }>;
  };
}

/** The most layouts `defaults.layouts` may list. */
export const MAX_DEFAULT_LAYOUTS = 8;

export interface SavedPayload {
  assetId: string;
  version: number;
  name: string;
}

export interface ErrorPayload {
  code: string;
  message: string;
}

/** Payload of each message type. */
export interface Payloads {
  'host:init': HostInit;
  'host:token': Token;
  'host:theme': { theme: Theme };
  'host:request-save': Record<string, never>;
  'host:request-close': Record<string, never>;
  'editor:ready': { versions: number[] };
  'editor:dirty': { dirty: boolean };
  'editor:saved': SavedPayload;
  'editor:close': { saved: boolean };
  'editor:close-cancelled': Record<string, never>;
  'editor:error': ErrorPayload;
}

export interface Envelope<T extends MessageType = MessageType> {
  protocol: typeof PROTOCOL;
  version: number;
  type: T;
  requestId?: string;
  payload: Payloads[T];
}

export type HostMessage = { [K in HostMessageType]: Envelope<K> }[HostMessageType];
export type EditorMessage = { [K in EditorMessageType]: Envelope<K> }[EditorMessageType];
export type Message = HostMessage | EditorMessage;

export type ValidationResult<M = Message> = { ok: true; message: M } | { ok: false; reason: string };

/** Logger the bridge reports to: message types and reasons only, never payloads or tokens. */
export interface BridgeLogger {
  debug?(message: string, details?: Record<string, unknown>): void;
  info?(message: string, details?: Record<string, unknown>): void;
  warn?(message: string, details?: Record<string, unknown>): void;
  error?(message: string, details?: Record<string, unknown>): void;
}

// ---------------------------------------------------------------------------------------------------------------
// Validation

const MAX_ID = 512;
const MAX_TEXT = 2000;
const MAX_TOKEN = 4096;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isString = (v: unknown, max = MAX_TEXT, min = 0): v is string => typeof v === 'string' && v.length >= min && v.length <= max;
const isId = (v: unknown): v is string => isString(v, MAX_ID, 1);
const isPositiveInt = (v: unknown, max = 1_000_000): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0 && v <= max;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/;
export const isIsoTime = (v: unknown): v is string => typeof v === 'string' && ISO.test(v) && !Number.isNaN(Date.parse(v));
const isTheme = (v: unknown): v is Theme => v === 'light' || v === 'dark';

/** Whether `m` looks like a bridge envelope (any version, any type): the shape check before validateMessage. */
export function isEnvelope(m: unknown): m is { protocol: typeof PROTOCOL; version: number; type: string; requestId?: string; payload: unknown } {
  return (
    isObject(m) &&
    m.protocol === PROTOCOL &&
    typeof m.version === 'number' &&
    Number.isInteger(m.version) &&
    typeof m.type === 'string' &&
    (m.requestId === undefined || isId(m.requestId)) &&
    'payload' in m
  );
}

/** Problems with a token, or undefined when it is valid. */
export function tokenProblem(t: unknown): string | undefined {
  if (!isObject(t)) return 'token must be an object';
  if (!isString(t.value, MAX_TOKEN, 1)) return 'token.value must be a non-empty string';
  if (!isIsoTime(t.expiresAt)) return 'token.expiresAt must be an ISO 8601 time';
  return undefined;
}

/** Problems with a host:init payload, or undefined when it is valid. */
export function initProblem(p: unknown): string | undefined {
  if (!isObject(p)) return 'payload must be an object';
  const tp = tokenProblem(p.token);
  if (tp) return tp;
  const u = p.user;
  if (!isObject(u) || !isId(u.id) || !isString(u.name, 500) || !isString(u.locale, 35)) return 'user must be { id, name, locale }';
  if (!isTheme(p.theme)) return "theme must be 'light' or 'dark'";
  if (p.mode !== 'new' && p.mode !== 'edit') return "mode must be 'new' or 'edit'";
  if (p.assetId !== undefined && !isId(p.assetId)) return 'assetId must be a string';
  if (p.mode === 'edit' && p.assetId === undefined) return "assetId is required when mode is 'edit'";
  if (p.folderId !== undefined && p.folderId !== null && !isId(p.folderId)) return 'folderId must be a string';
  if (p.defaults !== undefined) {
    const d = p.defaults;
    if (!isObject(d) || !isPositiveInt(d.width, 16384) || !isPositiveInt(d.height, 16384)) return 'defaults must be { width, height, frameRate }';
    const f = d.frameRate;
    if (!isObject(f) || !isPositiveInt(f.num) || !isPositiveInt(f.den)) return 'defaults.frameRate must be { num, den } (positive integers)';
    if (d.layouts !== undefined) {
      if (!Array.isArray(d.layouts) || d.layouts.length > MAX_DEFAULT_LAYOUTS) return `defaults.layouts must be an array of at most ${MAX_DEFAULT_LAYOUTS} { width, height }`;
      if (!d.layouts.every((l) => isObject(l) && isPositiveInt(l.width, 16384) && isPositiveInt(l.height, 16384))) return 'defaults.layouts must be { width, height } (positive integers, at most 16384)';
    }
  }
  return undefined;
}

const empty = (p: unknown) => (isObject(p) ? undefined : 'payload must be an object');

const PAYLOAD_CHECKS: Record<MessageType, (p: unknown) => string | undefined> = {
  'host:init': initProblem,
  'host:token': tokenProblem,
  'host:theme': (p) => (isObject(p) && isTheme(p.theme) ? undefined : "payload must be { theme: 'light' | 'dark' }"),
  'host:request-save': empty,
  'host:request-close': empty,
  'editor:ready': (p) =>
    isObject(p) && Array.isArray(p.versions) && p.versions.length > 0 && p.versions.length <= 32 && p.versions.every((v) => isPositiveInt(v, 1000))
      ? undefined
      : 'payload must be { versions: number[] }',
  'editor:dirty': (p) => (isObject(p) && typeof p.dirty === 'boolean' ? undefined : 'payload must be { dirty: boolean }'),
  'editor:saved': (p) =>
    isObject(p) && isId(p.assetId) && isPositiveInt(p.version, 1_000_000_000) && isString(p.name, 500) ? undefined : 'payload must be { assetId, version, name }',
  'editor:close': (p) => (isObject(p) && typeof p.saved === 'boolean' ? undefined : 'payload must be { saved: boolean }'),
  'editor:close-cancelled': empty,
  'editor:error': (p) => (isObject(p) && isString(p.code, 100, 1) && isString(p.message) ? undefined : 'payload must be { code, message }'),
};

/** Types that carry a requestId when they answer a request. */
const REQUESTS: ReadonlySet<string> = new Set(['host:request-save', 'host:request-close']);

/**
 * Validate a message: envelope, supported protocol version, known type (of the expected direction, when given)
 * and payload. The reason never contains payload values.
 */
export function validateMessage(m: unknown, direction: 'host'): ValidationResult<HostMessage>;
export function validateMessage(m: unknown, direction: 'editor'): ValidationResult<EditorMessage>;
export function validateMessage(m: unknown, direction?: 'host' | 'editor'): ValidationResult;
export function validateMessage(m: unknown, direction?: 'host' | 'editor'): ValidationResult {
  if (!isEnvelope(m)) return { ok: false, reason: 'not a prism-graphics envelope' };
  if (!SUPPORTED_VERSIONS.includes(m.version)) return { ok: false, reason: `unsupported protocol version ${m.version}` };
  const check = (PAYLOAD_CHECKS as Record<string, ((p: unknown) => string | undefined) | undefined>)[m.type];
  if (!check) return { ok: false, reason: 'unknown message type' };
  if (direction && !m.type.startsWith(`${direction}:`)) return { ok: false, reason: `not a ${direction} message` };
  if (REQUESTS.has(m.type) && m.requestId === undefined) return { ok: false, reason: `${m.type} needs a requestId` };
  const problem = check(m.payload);
  if (problem) return { ok: false, reason: `invalid ${m.type} payload: ${problem}` };
  return { ok: true, message: m as Message };
}

/** A new envelope. */
export function envelope<T extends MessageType>(type: T, payload: Payloads[T], requestId?: string): Envelope<T> {
  return { protocol: PROTOCOL, version: PROTOCOL_VERSION, type, ...(requestId !== undefined ? { requestId } : {}), payload };
}

/** A request id: random, URL-safe, unique per page. */
export function newRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** The message type for a log line: only a known type is named (never attacker-chosen text). */
export function loggableType(m: unknown): string | undefined {
  const t = isObject(m) ? m.type : undefined;
  return typeof t === 'string' && (HOST_MESSAGE_TYPES as readonly string[]).concat(EDITOR_MESSAGE_TYPES).includes(t) ? t : undefined;
}
